import {AbstractService} from "./abstract.service";
import {CurrencyRepo} from "../repositories/api/currency.repo";
import {inject, Injectable} from "@angular/core";
import {map} from "rxjs/operators";
import {FormatService} from "./format.service";
import {BehaviorSubject, Observable} from "rxjs";
import {CurrencyDto} from "../repositories/dto/currency.dto";
import {formatCurrency} from "@angular/common";
import {CalcService} from "./calc.service";
import {isDefined} from "../libs/utils/epic.utils";

@Injectable({providedIn: 'root'})
export class CurrencyService extends AbstractService {

  private readonly calc: CalcService = inject(CalcService);
  private readonly currencyRepo: CurrencyRepo = this.repoProvider.getRepo(CurrencyRepo);

  private readonly currentCurrencies = new BehaviorSubject<CurrencyDto[]>([]);
  private readonly allCurrencies = new BehaviorSubject<CurrencyDto[]>([]);
  private readonly defaultCurrency = new BehaviorSubject<CurrencyDto | undefined>(undefined);
  private currentLanguageCode: string | undefined;
  private orgLanguageCode: string | undefined;

  constructor() {
    super();
    this.stateService.getOrgDistinct().subscribe((org) => {
      if (isDefined(org)) {
        this.defaultCurrency.next(org.defaultCurrency);
        this.currencyRepo.readCurrent(org)
          .pipe(map((w) => w.getDatas()))
          .subscribe((c) => this.currentCurrencies.next(c));
        this.currencyRepo.readAll(org)
          .pipe(map((w) => w.getDatas()))
          .subscribe((c) => this.allCurrencies.next(c));
        this.orgLanguageCode = org.languageCode;
      } else {
        this.defaultCurrency.next(undefined);
        this.currentCurrencies.next([]);
        this.allCurrencies.next([]);
      }
    });
    this.i18n.getCurrentLanguageCode().subscribe((lang) => this.currentLanguageCode = lang);
  }

  public getAll(): Observable<CurrencyDto[]> {
    return this.allCurrencies.asObservable();
  }

  public getCurrents(): Observable<CurrencyDto[]> {
    return this.currentCurrencies.asObservable();
  }

  public getDefault(): Observable<CurrencyDto | undefined> {
    return this.defaultCurrency.asObservable();
  }

  public getDefaultCurrency(): CurrencyDto | undefined {
    return this.defaultCurrency.value;
  }

  public getByCurrencyCode(code: string): CurrencyDto | undefined {
    if (code === CurrencyDto.NONE.code) {
      return CurrencyDto.NONE;
    } else if (this.allCurrencies.value.length > 0) {
      return this.allCurrencies.value.find((c) => c.code === code);
    }
    return this.currentCurrencies.value.find((c) => c.code === code);
  }

  public clearValue(value: string): string {
    const curValue = value.replace(/[^-?\d,\\.]/gs, '');
    const match = Array.from(curValue.matchAll(/^-?(\d*)[,\\.](\d{0,5})(\d*)/gs), m => m[3])[0];
    let resultValue = curValue;
    if (isDefined(match) && match.length > 0) {
      resultValue = curValue.substring(0, curValue.length - match.length);
    }
    return resultValue.replace(',', '.');
  }

  public format2Number(value: string, fractionDigits?: number): number {
    const curValue: string = this.clearValue(value);
    const curNo: number = parseFloat(curValue.replace(',', '.'));
    const digits: number = fractionDigits ? fractionDigits : FormatService.DEFAULT_FRACTION_DIGITS;
    return this.calc.toFixed(curNo, digits);
  }

  public format(value: number | undefined, currency?: CurrencyDto, fractionDigits?: number): string | undefined {
    if (isDefined(value)) {
      const symbol: string | undefined = currency ? currency.symbol : this.getDefaultCurrency()?.symbol;
      const code: string | undefined = currency ? currency.code : this.getDefaultCurrency()?.code;
      if (symbol && code && this.currentLanguageCode) {
        const useValue: number = fractionDigits ? this.calc.toFixed(value, fractionDigits) : value;
        return formatCurrency(useValue, this.currentLanguageCode, symbol, code, FormatService.DIGITS_INFO);
      }
    }
    return undefined;
  }

  public formatAmount(value: number | undefined, currencyCode: string, fractionalDigits: number): string | undefined {
    if (isDefined(value)) {
      const currency: CurrencyDto | undefined = this.getByCurrencyCode(currencyCode);
      const sym: string | undefined = isDefined(currency) ? currency.symbol : this.getDefaultCurrency()?.symbol;
      const symbol: string = isDefined(sym) ? sym : '';
      const languageCode: string | undefined = isDefined(this.orgLanguageCode) ? this.orgLanguageCode : this.currentLanguageCode;
      if (currency?.code === 'EUR') {
        return formatCurrency(value, 'de', symbol, currencyCode, '1.' + fractionalDigits);
      } else if (currency?.code === 'GEL') {
        return formatCurrency(value, 'ka', symbol, currencyCode, '1.' + fractionalDigits);
      }
      switch (languageCode) {
        // @ts-ignore
        default:
          this.log.warn('Not implemented language code "{}" - use fallback "en"', [languageCode]);
        // eslint-disable-next-line no-fallthrough
        case 'en':
          return formatCurrency(value, 'en', symbol, currencyCode, '1.' + fractionalDigits);
        case 'de':
          return formatCurrency(value, 'de', symbol, currencyCode, '1.' + fractionalDigits);
      }
    }
    return undefined;
  }
}

